import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { syncHeaderCartCount } from '@theme/gullyo-header-cart-count';
import { viewTransition } from '@theme/utilities';

/** @type {Promise<void> | null} */
let sectionsUpdateInFlight = null;

/** @type {Promise<Record<string, any>> | null} */
let cartFetchInFlight = null;

/** @type {number} */
let pendingCartAdds = 0;

/** @type {Array<() => void>} */
let pendingCartAddWaiters = [];

/**
 * @param {string} [source]
 */
export function beginCartAdd(source = 'unknown') {
  pendingCartAdds += 1;
  document.documentElement.dataset.gullyoCartAddPending = 'true';
  document.dispatchEvent(
    new CustomEvent('gullyo:cart-add-pending', {
      bubbles: true,
      detail: { pending: pendingCartAdds, source },
    })
  );
}

/**
 * @param {{ success?: boolean }} [options]
 */
export function endCartAdd(options = {}) {
  pendingCartAdds = Math.max(0, pendingCartAdds - 1);

  if (pendingCartAdds === 0) {
    delete document.documentElement.dataset.gullyoCartAddPending;
    const waiters = pendingCartAddWaiters.splice(0);
    waiters.forEach((resolve) => resolve());
  }

  document.dispatchEvent(
    new CustomEvent('gullyo:cart-add-settled', {
      bubbles: true,
      detail: { pending: pendingCartAdds, success: options.success !== false },
    })
  );
}

/**
 * @returns {boolean}
 */
export function isCartAddPending() {
  return pendingCartAdds > 0;
}

/**
 * @returns {Promise<void>}
 */
export function waitForPendingCartAdds() {
  if (pendingCartAdds === 0) return Promise.resolve();
  return new Promise((resolve) => {
    pendingCartAddWaiters.push(resolve);
  });
}

/**
 * Wait for any in-progress View Transition to finish (avoids InvalidStateError during cart morph).
 */
export async function awaitViewTransitionReady() {
  if (!viewTransition.current) return;

  try {
    await viewTransition.current;
  } catch {
    // Transition aborted — safe to continue cart updates.
  }
}

/**
 * @param {string} html
 * @returns {boolean}
 */
function sectionHtmlHasCartItems(html) {
  if (!html) return false;
  return (
    html.includes('gullyo-cart-item') ||
    html.includes('cart-items__table-row') ||
    html.includes('cart-form')
  );
}

/**
 * @param {HTMLElement} component
 * @returns {boolean}
 */
function componentHasCartItems(component) {
  return Boolean(
    component.querySelector('.cart-form') ||
      component.querySelector('.gullyo-cart-item') ||
      component.querySelector('.cart-items__table-row')
  );
}

/**
 * Return one shared live-cart request to all concurrent reconciliation paths.
 *
 * @returns {Promise<Record<string, any>>}
 */
export async function fetchLiveCart() {
  if (cartFetchInFlight) return cartFetchInFlight;

  cartFetchInFlight = fetch('/cart.js', {
    credentials: 'same-origin',
    headers: { Accept: 'application/json' },
  }).then((response) => {
    if (!response.ok) throw new Error('Cart fetch failed');
    return response.json();
  });

  try {
    return await cartFetchInFlight;
  } finally {
    cartFetchInFlight = null;
  }
}

/**
 * @returns {Promise<number>}
 */
async function fetchLiveCartCount() {
  const cart = await fetchLiveCart();
  return cart.item_count ?? 0;
}

/**
 * @param {string} html
 * @returns {number | null}
 */
export function extractCartItemCountFromSectionHtml(html) {
  if (!html) return null;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const countText = doc.querySelector('[ref="cartItemCount"]')?.textContent?.trim();
  if (!countText) return null;
  return parseInt(countText, 10) || 0;
}

/**
 * @returns {HTMLElement[]}
 */
export function getCartItemsComponents() {
  return Array.from(document.querySelectorAll('cart-items-component[data-section-id]')).filter(
    (node) => node instanceof HTMLElement
  );
}

/**
 * @param {HTMLElement} component
 */
export function syncDrawerEmptyStateForComponent(component) {
  if (!(component instanceof HTMLElement) || component.dataset.drawer === undefined) return;

  const drawer = component.closest('cart-drawer-component');
  const dialog = drawer?.querySelector('dialog');
  if (!(dialog instanceof HTMLDialogElement)) return;

  if (isCartAddPending() || dialog.classList.contains('cart-drawer--adding')) {
    dialog.classList.remove('cart-drawer--empty');
    dialog.setAttribute('aria-labelledby', 'cart-drawer-heading');
    return;
  }

  const isEmpty = !componentHasCartItems(component);
  dialog.classList.toggle('cart-drawer--empty', isEmpty);
  dialog.setAttribute('aria-labelledby', isEmpty ? 'cart-drawer-heading-empty' : 'cart-drawer-heading');
}

/**
 * @param {string} sectionId
 * @param {string} html
 * @param {'hydration' | 'full'} mode
 */
export async function safeMorphSection(sectionId, html, mode = 'full') {
  await awaitViewTransitionReady();
  await morphSection(sectionId, html, mode);
}

/**
 * @param {Record<string, string>} sections
 * @param {{ fallbackCount?: number }} [options]
 * @returns {Promise<number | null>}
 */
export async function applyCartSectionsUpdate(sections, options = {}) {
  if (!sections || typeof sections !== 'object') {
    return refreshCartSections();
  }

  if (sectionsUpdateInFlight) {
    await sectionsUpdateInFlight;
  }

  sectionsUpdateInFlight = (async () => {
    await awaitViewTransitionReady();

    let latestCount = options.fallbackCount ?? null;

    for (const component of getCartItemsComponents()) {
      const sectionId = component.dataset.sectionId;
      const html = sectionId ? sections[sectionId] : null;
      if (!sectionId || !html) continue;

      const wasEmpty = !componentHasCartItems(component);
      const isDrawer = component.dataset.drawer !== undefined;
      const incomingHasItems = sectionHtmlHasCartItems(html);
      const mode = isDrawer && !wasEmpty && !incomingHasItems ? 'hydration' : 'full';

      try {
        await morphSection(sectionId, html, mode);
      } catch (error) {
        console.error('[gullyo-cart-sync] Section morph failed:', error);
        await sectionRenderer.renderSection(sectionId, { cache: false });
      }

      const escapedSectionId = window.CSS?.escape ? window.CSS.escape(sectionId) : sectionId;
      const currentComponent =
        document.querySelector(`cart-items-component[data-section-id="${escapedSectionId}"]`) || component;
      syncDrawerEmptyStateForComponent(currentComponent);

      if ('updatedCallback' in currentComponent && typeof currentComponent.updatedCallback === 'function') {
        currentComponent.updatedCallback();
      }
    }

    try {
      latestCount = await fetchLiveCartCount();
    } catch {
      latestCount = latestCount ?? 0;
    }

    const drawer = getCartItemsComponents().find((component) => component.dataset.drawer !== undefined);
    if (drawer instanceof HTMLElement && latestCount > 0 && !componentHasCartItems(drawer)) {
      const sectionId = drawer.dataset.sectionId;
      if (sectionId) {
        try {
          await sectionRenderer.renderSection(sectionId, { cache: false });
        } catch (error) {
          console.error('[gullyo-cart-sync] Drawer section refresh failed:', error);
        }
        syncDrawerEmptyStateForComponent(drawer);
      }
    }

    syncHeaderCartCount(latestCount);
    return latestCount;
  })();

  try {
    return await sectionsUpdateInFlight;
  } finally {
    sectionsUpdateInFlight = null;
  }
}

/**
 * @returns {Promise<number | null>}
 */
export async function refreshCartSections() {
  const components = getCartItemsComponents();
  const sectionIds = components.map((component) => component.dataset.sectionId).filter(Boolean);

  if (!sectionIds.length) return null;

  await awaitViewTransitionReady();

  for (const sectionId of sectionIds) {
    if (!sectionId) continue;
    await sectionRenderer.renderSection(sectionId, { cache: false });
    const component = document.querySelector(`cart-items-component[data-section-id="${sectionId}"]`);
    if (component instanceof HTMLElement) {
      syncDrawerEmptyStateForComponent(component);
    }
  }

  try {
    const count = await fetchLiveCartCount();
    syncHeaderCartCount(count);
    return count;
  } catch {
    return null;
  }
}

window.GullyoCartSync = {
  applyCartSectionsUpdate,
  refreshCartSections,
  syncHeaderCartCount,
  awaitViewTransitionReady,
  safeMorphSection,
  syncDrawerEmptyStateForComponent,
    fetchLiveCartCount,
    fetchLiveCart,
    beginCartAdd,
  endCartAdd,
  isCartAddPending,
  waitForPendingCartAdds,
};

/**
 * Keep header badge aligned with /cart.js on cart page load and navigation restores.
 */
function initHeaderCartCountReconciliation() {
  const reconcile = async () => {
    try {
      const count = await fetchLiveCartCount();
      syncHeaderCartCount(count);

      const cartPage = document.querySelector('[data-template="cart"]');
      const emptyCartUi = document.querySelector('.cart-page--empty, .cart-drawer--empty');
      if (cartPage && (count === 0 || emptyCartUi)) {
        syncHeaderCartCount(0);
      }
    } catch {
      // no-op
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile, { once: true });
  } else {
    reconcile();
  }

  window.addEventListener('pageshow', reconcile);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      reconcile();
    }
  });
}

initHeaderCartCountReconciliation();
